import {
  useEffect,
  useRef,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import {
  API_URL,
  getPosHeaders,
} from "./api";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ENDPOINTS = {
  warehouses: "/pos/warehouses/me",
  template: "/pos/inventory/excel/template",
  import: "/pos/inventory/excel/import",
};

function getItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;

  return [];
}

async function getErrorMessage(error, fallback) {
  let detail = error.response?.data?.detail;

  // Al descargar Excel, Axios también recibe los
  // mensajes de error del servidor como Blob.
  if (error.response?.data instanceof Blob) {
    try {
      const content =
        await error.response.data.text();

      const parsed = JSON.parse(content);

      detail = parsed.detail;
    } catch {
      // Conserva el mensaje general.
    }
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (Array.isArray(detail)) {
    return (
      detail
        .map((item) => item.msg)
        .filter(Boolean)
        .join(". ") || fallback
    );
  }

  if (error.response?.status === 401) {
    return "La sesión POS venció. Inicia sesión nuevamente.";
  }

  if (error.response?.status === 403) {
    return "La sesión no tiene acceso a esta operación.";
  }

  if (error.response?.status === 404) {
    return (
      "No se encontró la ruta de inventario Excel. " +
      "Verifica que pos_inventory_excel esté registrado en main.py."
    );
  }

  return fallback;
}

export default function InventoryExcelPOS({
  onCompleted,
}) {
  const { company } = useParams();

  const fileInputRef = useRef(null);
  const operationRef = useRef(null);
  const companyRef = useRef(company);

  companyRef.current = company;

  const [warehouses, setWarehouses] = useState([]);

  const [
    selectedWarehouseId,
    setSelectedWarehouseId,
  ] = useState("");

  const [
    loadingWarehouses,
    setLoadingWarehouses,
  ] = useState(true);

  const [
    warehousesError,
    setWarehousesError,
  ] = useState("");

  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const [
    warehousesVersion,
    setWarehousesVersion,
  ] = useState(0);

  // Cargar las bodegas activas de la empresa.
  useEffect(() => {
    const controller = new AbortController();

    setLoadingWarehouses(true);
    setWarehousesError("");
    setError("");
    setResult(null);
    setWarehouses([]);
    setSelectedWarehouseId("");

    async function loadWarehouses() {
      try {
        const response = await axios.get(
          `${API_URL}${ENDPOINTS.warehouses}`,
          {
            headers: getPosHeaders(company),
            params: {
              active_only: true,
            },
            signal: controller.signal,
          }
        );

        if (controller.signal.aborted) return;

        const items = getItems(response.data);

        setWarehouses(items);

        const defaultWarehouse =
          items.find(
            (warehouse) => warehouse.is_default
          ) || items[0];

        setSelectedWarehouseId(
          defaultWarehouse
            ? String(defaultWarehouse.id)
            : ""
        );

        if (items.length === 0) {
          setWarehousesError(
            "No hay bodegas activas disponibles."
          );
        }
      } catch (requestError) {
        if (controller.signal.aborted) return;

        const message = await getErrorMessage(
          requestError,
          "No fue posible cargar las bodegas."
        );

        if (!controller.signal.aborted) {
          setWarehousesError(message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingWarehouses(false);
        }
      }
    }

    loadWarehouses();

    return () => controller.abort();
  }, [
    company,
    warehousesVersion,
  ]);

  // Evita actualizar este componente después de
  // desmontarlo o de cambiar de empresa.
  useEffect(() => {
    return () => {
      operationRef.current = null;
    };
  }, [company]);

  const selectedWarehouse = warehouses.find(
    (warehouse) =>
      String(warehouse.id) === selectedWarehouseId
  );

  const isBusy = Boolean(busy);

  const downloadProductsTemplate = async () => {
    if (operationRef.current) return;

    if (!selectedWarehouseId) {
      setError(
        "Selecciona una bodega para descargar la plantilla."
      );
      return;
    }

    const operation = {};
    const currentCompany = company;

    operationRef.current = operation;

    const isCurrentOperation = () =>
      operationRef.current === operation &&
      companyRef.current === currentCompany;

    setBusy("download");
    setError("");

    try {
      const response = await axios.get(
        `${API_URL}${ENDPOINTS.template}`,
        {
          headers: getPosHeaders(company),
          params: {
            warehouse_id: Number(
              selectedWarehouseId
            ),
          },
          responseType: "blob",
        }
      );

      if (!isCurrentOperation()) return;

      const blobUrl = URL.createObjectURL(
        response.data
      );

      try {
        const link = document.createElement("a");

        link.href = blobUrl;
        link.download = "plantilla_inventario.xlsx";

        document.body.appendChild(link);
        link.click();
        link.remove();
      } finally {
        window.setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      }

      toast.success(
        "Plantilla de inventario descargada."
      );
    } catch (requestError) {
      if (!isCurrentOperation()) return;

      const message = await getErrorMessage(
        requestError,
        "No fue posible descargar la plantilla."
      );

      if (isCurrentOperation()) {
        setError(message);
        toast.error(message);
      }
    } finally {
      if (isCurrentOperation()) {
        operationRef.current = null;
        setBusy("");
      }
    }
  };

  const importProductsExcel = async (event) => {
    const input = event.target;
    const file = input.files?.[0];

    // Permite volver a elegir el mismo archivo.
    input.value = "";

    if (!file || operationRef.current) {
      return;
    }

    setError("");
    setResult(null);

    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      setError(
        "Selecciona un archivo Excel con extensión .xlsx."
      );
      return;
    }

    if (file.size === 0) {
      setError("El archivo está vacío.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError(
        "El archivo no puede superar los 5 MB."
      );
      return;
    }

    const confirmed = window.confirm(
      `¿Importar el archivo "${file.name}"?\n\n` +
        "Se registrarán entradas de inventario, lotes " +
        "y los asientos correspondientes a la " +
        "configuración contable actual.\n\n" +
        "La bodega se tomará del código escrito en " +
        "cada fila del Excel.\n\n" +
        "Verifica las cantidades y los costos antes " +
        "de continuar."
    );

    if (!confirmed) return;

    const operation = {};
    const currentCompany = company;

    operationRef.current = operation;

    const isCurrentOperation = () =>
      operationRef.current === operation &&
      companyRef.current === currentCompany;

    setBusy("import");

    let imported = false;

    try {
      const formData = new FormData();

      formData.append("file", file);

      const response = await axios.post(
        `${API_URL}${ENDPOINTS.import}`,
        formData,
        {
          headers: getPosHeaders(company),
        }
      );

      if (!isCurrentOperation()) return;

      const data = response.data || {};

      const normalizedResult = {
        created: Number(data.created) || 0,
        skipped: Array.isArray(data.skipped)
          ? data.skipped
          : [],
        message:
          data.message ||
          "Importación de inventario finalizada.",
      };

      setResult(normalizedResult);

      if (normalizedResult.created > 0) {
        toast.success(normalizedResult.message);
      } else {
        toast.info(normalizedResult.message);
      }

      imported = true;
    } catch (requestError) {
      if (!isCurrentOperation()) return;

      const message = await getErrorMessage(
        requestError,
        "No fue posible importar el inventario. " +
          "Revisa la conexión y consulta el historial " +
          "antes de reintentar."
      );

      if (isCurrentOperation()) {
        setError(message);
        toast.error(message);
      }
    } finally {
      if (isCurrentOperation()) {
        operationRef.current = null;
        setBusy("");
      }
    }

    if (
      imported &&
      companyRef.current === currentCompany
    ) {
      try {
        await onCompleted?.();
      } catch {
        toast.warning(
          "La importación terminó, pero no fue posible " +
            "actualizar la pantalla. Pulsa Actualizar."
        );
      }
    }
  };

  return (
    <section
      className="inventory-card movement-form-card inventory-excel-card"
      aria-labelledby="inventory-excel-title"
      aria-busy={isBusy}
    >
      <div className="section-title">
        <div>
          <h2 id="inventory-excel-title">
            Cargar inventario desde Excel
          </h2>

          <p>
            Descarga la plantilla, revisa las existencias
            e importa varios productos en una sola carga.
          </p>
        </div>
      </div>

      <div className="inventory-form-grid">
        <div className="inventory-field">
          <label htmlFor="inventory-excel-warehouse">
            Bodega para la plantilla
          </label>

          <select
            id="inventory-excel-warehouse"
            value={selectedWarehouseId}
            onChange={(event) => {
              setSelectedWarehouseId(
                event.target.value
              );
              setError("");
            }}
            disabled={
              loadingWarehouses ||
              isBusy
            }
          >
            <option value="">
              {loadingWarehouses
                ? "Cargando bodegas..."
                : "Selecciona una bodega"}
            </option>

            {warehouses.map((warehouse) => (
              <option
                key={warehouse.id}
                value={warehouse.id}
              >
                {warehouse.name}
                {warehouse.is_default
                  ? " · Principal"
                  : ""}
              </option>
            ))}
          </select>

          {selectedWarehouse && (
            <small className="selected-stock">
              Código de bodega:{" "}
              <strong>
                {selectedWarehouse.code || "—"}
              </strong>
            </small>
          )}
        </div>
      </div>

      {warehousesError && (
        <div
          className="alert alert-warning"
          role="alert"
        >
          <p>{warehousesError}</p>

          <button
            type="button"
            className="btn-clear-filters"
            disabled={
              loadingWarehouses ||
              isBusy
            }
            onClick={() =>
              setWarehousesVersion(
                (current) => current + 1
              )
            }
          >
            Reintentar cargar bodegas
          </button>
        </div>
      )}

      <div
        className="product-import-actions"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <button
          type="button"
          className="btn-download-template"
          onClick={downloadProductsTemplate}
          disabled={
            loadingWarehouses ||
            isBusy ||
            !selectedWarehouseId
          }
        >
          {busy === "download"
            ? "Descargando..."
            : "Descargar plantilla de inventario"}
        </button>

        <button
          type="button"
          className="btn-import-excel"
          onClick={() =>
            fileInputRef.current?.click()
          }
          disabled={
            loadingWarehouses ||
            isBusy ||
            warehouses.length === 0
          }
        >
          {busy === "import"
            ? "Importando..."
            : "Importar inventario desde Excel"}
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          hidden
          onChange={importProductsExcel}
        />
      </div>

      <div className="inventory-excel-instructions">
        <p>
          <strong>La plantilla incluye:</strong>{" "}
          SKU, código de bodega, cantidad propuesta
          de 300 unidades y costo de compra registrado.
        </p>

        <p>
          Reemplaza las cantidades por las existencias
          reales y revisa los costos antes de importar.
          Conserva el SKU y el código de bodega como texto.
        </p>

        <p>
          <small>
            Solo se cargan productos activos sin variantes,
            sin existencias y sin movimientos previos en
            esa bodega. La importación utiliza la bodega
            indicada en cada fila del archivo.
            Formato .xlsx, máximo 5 MB y 1000 filas.
          </small>
        </p>
      </div>

      {busy === "import" && (
        <p
          className="inventory-excel-status"
          role="status"
        >
          Registrando existencias y movimientos.
          Espera a que termine la importación.
        </p>
      )}

      {error && (
        <div
          className="alert alert-danger"
          role="alert"
        >
          {error}
        </div>
      )}

      {result && (
        <div className="inventory-excel-result">
          <p role="status">
            <strong>
              {result.message}
            </strong>
          </p>

          <div className="inventory-summary-grid">
            <article className="inventory-summary-card stock-total">
              <span>Productos cargados</span>
              <strong>
                {result.created}
              </strong>
              <small>
                Entradas registradas
              </small>
            </article>

            <article className="inventory-summary-card low-stock">
              <span>Productos omitidos</span>
              <strong>
                {result.skipped.length}
              </strong>
              <small>
                Consulta el motivo de cada fila
              </small>
            </article>
          </div>

          {result.skipped.length > 0 && (
            <details
              className="inventory-excel-skipped"
              style={{ marginTop: 16 }}
            >
              <summary>
                Ver productos omitidos
              </summary>

              <div className="inventory-table-wrapper">
                <table className="inventory-table">
                  <thead>
                    <tr>
                      <th>Fila del Excel</th>
                      <th>SKU</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {result.skipped.map(
                      (item, index) => (
                        <tr
                          key={`${item.row}:${item.sku}:${index}`}
                        >
                          <td>
                            {item.row ?? "—"}
                          </td>

                          <td>
                            {item.sku || "—"}
                          </td>

                          <td>
                            {item.reason ||
                              "Producto omitido."}
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
}